import * as THREE from 'three';
import ClipperLib from '../../../3dapps/libs/clipper_unminified';

function vector2ToFPoint(p) {
    return new ClipperLib.FPoint(p.x, p.y);
}

export class PaintDrawer {
    boundWidth = 100;
    boundHeight = 100;
    lineWidth = 8.0; // 画笔粗细
    penType = "add";
    sizeUpdated = false;
    historyList = []; // 历史记录
    historyIndex = -1; // 历史记录下标

    constructor(canvas) {
        this.canvas = canvas;
        this.context = canvas.getContext("2d");
    }

    updateSize() {
        if (this.sizeUpdated) return;

        this.sizeUpdated = true;
        let bound = this.canvas.parentNode.getBoundingClientRect();
        let width = Math.floor(bound.width);
        let height = Math.floor(bound.height);
        this.canvas.style.width = width + "px";
        this.canvas.style.height = height + "px";
        this.canvas.width = width;
        this.canvas.height = height;

        this.currPath = [];

        this.width = this.canvas.width;
        this.height = this.canvas.height;
        this.centerX = Math.round(this.width / 2);
        this.centerY = Math.round(this.height / 2);
        this.context.translate(this.centerX + 0.5, this.centerY + 0.5);

        const padding = 20;
        let maxWidth = this.width - padding * 2;
        let maxHeight = this.height - padding * 2;
        let ratio1 = maxWidth / maxHeight;
        let boundWidth = this.boundWidth;
        let boundHeight = this.boundHeight;
        let ratio2 = boundWidth / boundHeight;
        let actualWidth, actualHeight;
        if (ratio1 <= ratio2) {
            actualWidth = maxWidth;
            actualHeight = actualWidth / ratio2;
        } else {
            actualHeight = maxHeight;
            actualWidth = actualHeight * ratio2;
        }
        this.scale = boundWidth / actualWidth;
        this.boundRect = [
            { X: -actualWidth / 2, Y: -actualHeight / 2 },
            { X: -actualWidth / 2, Y: actualHeight / 2 },
            { X: actualWidth / 2, Y: actualHeight / 2 },
            { X: actualWidth / 2, Y: -actualHeight / 2 }
        ];

        this.mouse_pos = new THREE.Vector2(); // 鼠标当前位置
        this.lastClickTime = 0;

        this.start_drag_pos = new THREE.Vector2(); // 鼠标拖动时的初始位置
        this.drag_target_index = -1; // 鼠标拖动的对象
        this.drag_target_start_pos = new THREE.Vector2(); // 拖动对象的初始位置
        this.drag_vector = new THREE.Vector2(); // 拖动矢量

        const editor = this;
        this.paintLine = [];

        this.canvas.addEventListener("touchstart", function (e) {
            console.log(e);
            let x = e.touches[0].clientX - bound.left;
            let y = e.touches[0].clientY - bound.top;
            editor.mouse_pos.x = x - editor.centerX;
            editor.mouse_pos.y = y - editor.centerY;
            editor.startPaint();
            editor.draw();
        });
        this.canvas.addEventListener("touchmove", function (e) {
            let x = e.touches[0].clientX - bound.left;
            let y = e.touches[0].clientY - bound.top;
            editor.mouse_pos.x = x - editor.centerX;
            editor.mouse_pos.y = y - editor.centerY;

            if (editor.isPainting) {
                editor.doPaint();
                editor.draw();
            }
        });
        this.canvas.addEventListener("touchend", function (e) {
            if (editor.isPainting) {
                editor.endPaint();
                editor.draw();
                if (editor.onChange) {
                    editor.onChange();
                }
            }
        });
        this.canvas.addEventListener("mouseleave", function (e) {
            if (e.button !== 0) return;

            if (editor.isPainting) {
                editor.endPaint();
                editor.draw();
                if (editor.onChange) {
                    editor.onChange();
                }
            }
        });

        this.draw();
    }

    startPaint() {
        this.isPainting = true;
        this.paintLine = [ vector2ToFPoint(this.mouse_pos) ];
    }

    doPaint() {
        this.paintLine.push(vector2ToFPoint(this.mouse_pos));
    }

    unionNewPaths() {
        // 先计算两个自交
        let c = new ClipperLib.Clipper();
        let joinPath = new ClipperLib.PolyTree();
        c.AddPaths(this.paintPath, ClipperLib.PolyType.ptSubject, true);
        c.AddPaths(this.paintPath.map(path => path.map(p => new ClipperLib.FPoint(-p.X, p.Y))), ClipperLib.PolyType.ptClip, true);
        c.Execute(ClipperLib.ClipType.ctUnion, joinPath);

        return {
            resultPolyTree: joinPath,
            resultPaths: ClipperLib.Clipper.ClosedPathsFromPolyTree(joinPath)
        };
    }

    polyTreeToPathList(polytree) {
        let pathList = [];
        let fPointToVector2 = p => new THREE.Vector2(-p.X, -p.Y);
        for (let i = 0, nodes = polytree.Childs(); i < nodes.length; i++) {
            let node = nodes[i];
            let path = {
                shape: node.Contour().map(fPointToVector2),
                holes: node.Childs().map(holeNode => holeNode.Contour().map(fPointToVector2))
            };
            pathList.push(path);
            node.Childs().forEach(subNode => { nodes.push(...subNode.Childs()); });
        }
        return pathList;
    }

    addHistoryRecord() {
        if (this.historyIndex < this.historyList.length - 1) {
            this.historyList.splice(this.historyList + 1);
        }
        this.historyList.push({ path: this.currPath, pathList: this.currPathList });
        this.historyIndex = this.historyList.length - 1;
    }

    endPaint() {
        this.isPainting = false;
        if (this.currPath.length === 0) {
            if (this.penType === "add") {
                let result = this.unionNewPaths();
                this.currPath = result.resultPaths;
                this.currPathList = this.polyTreeToPathList(result.resultPolyTree);
                this.addHistoryRecord();
            } else {
                this.currPath = [];
                this.currPathList = [];
                this.addHistoryRecord();
            }
        } else {
            if (this.penType === "add") {
                let solution = new ClipperLib.PolyTree();
                let c = new ClipperLib.Clipper();
                c.AddPaths(this.currPath, ClipperLib.PolyType.ptSubject, true); // 现有的路径
                let newPaths = this.unionNewPaths().resultPaths;
                c.AddPaths(newPaths, ClipperLib.PolyType.ptClip, true); // 现有的路径
                c.Execute(ClipperLib.ClipType.ctUnion, solution);

                this.currPathList = this.polyTreeToPathList(solution);
                this.currPath = ClipperLib.Clipper.ClosedPathsFromPolyTree(solution);
            } else {
                let solution = new ClipperLib.PolyTree();
                let c = new ClipperLib.Clipper();
                c.AddPaths(this.currPath, ClipperLib.PolyType.ptSubject, true);
                let newPaths = this.unionNewPaths().resultPaths;
                c.AddPaths(newPaths, ClipperLib.PolyType.ptClip, true);
                c.Execute(ClipperLib.ClipType.ctDifference, solution);
                this.currPathList = this.polyTreeToPathList(solution);
                this.currPath = ClipperLib.Clipper.ClosedPathsFromPolyTree(solution);
            }
            this.addHistoryRecord();
        }
        this.paintLine = [];

        if (this.onChange) { this.onChange(); }
    }

    // 回退一步
    goBack() {
        if (this.historyIndex < 0) return;
        
        this.historyIndex--;
        if (this.historyIndex >= 0) {
            let item = this.historyList[this.historyIndex];
            if (!item) console.error("history item is empty", item, this.historyList, this.historyIndex);
            console.log(item);
            this.currPath = item.path;
            this.currPathList = item.pathList;
        } else {
            this.currPath = [];
            this.currPathList = [];
        }
        this.draw();
        this.onChange();
    }

    // 前进一步
    goForward() {
        if (this.historyIndex < this.historyList.length - 1) {
            this.historyIndex++;
            let item = this.historyList[this.historyIndex];
            this.currPath = item.path;
            this.currPathList = item.pathList;
            this.draw();
            this.onChange();
        }
    }

    clearAll() {
        if (this.currPath.length > 0) {
            this.currPath = [];
            this.currPathList = [];
            this.addHistoryRecord();
            this.draw();
            this.onChange();
        }
    }

    getData() {
        let data = {
            historyList: this.historyList,
            historyIndex: this.historyIndex,
        };
        return data;
    }

    setData(data) {
        this.historyList = data.historyList.map(item => {
            console.log(item);
            return {
                path: item.path,
                pathList: item.pathList.map(path => {
                    return {
                        shape: path.shape.map(p => new THREE.Vector2(p.x, p.y)),
                        holes: path.holes.map(hole => hole.map(p => new THREE.Vector2(p.x, p.y)))
                    }
                })
            };
        });
        this.historyIndex = data.historyIndex;
        if (this.historyList.length === 0) {
            this.currPath = [];
            this.currPathList = [];
        } else {
            let currItem = this.historyList[this.historyIndex];
            this.currPath = currItem.path;
            this.currPathList = currItem.pathList;
        }
        this.draw();
        this.onChange();
    }

    getCurrData() {
        return this.historyList[this.historyIndex];
    }

    setCurrData(data) {
        this.historyList = [data];
        this.historyIndex = 0;
        this.currPath = data.path;
        this.currPathList = data.pathList;
        this.draw();
        this.onChange();
    }

    draw() {
        this.clear();
        this.drawObjects();
        this.drawPaint();
        this.drawAxes();
    }

    clear() {
        this.context.clearRect(-this.centerX - 1, - this.centerY - 1, this.canvas.width + 2, this.canvas.height + 2);
    }

    // 对称轴
    drawAxes() {
        let centerY = this.centerY;
        this.context.beginPath();
        this.context.moveTo(0, -centerY - 1);
        this.context.lineTo(0, centerY + 1);
        this.context.lineWidth = 1;
        this.context.setLineDash([10, 10]);
        this.context.strokeStyle = "black";
        this.context.stroke();
    }

    drawBound() {
        let centerX = this.centerX;
        let centerY = this.centerY;
        this.context.strokeStyle = "red";
        this.context.lineWidth = 2;
        this.context.beginPath();
        this.context.moveTo(this.boundRect[0].X + centerX, this.boundRect[0].Y + centerY);
        for (let i = 1; i < this.boundRect.length; i++) {
            this.context.lineTo(this.boundRect[i].X + centerX, this.boundRect[i].Y + centerY);
        }
        this.context.closePath();
        this.context.stroke();
    }

    drawObjects() {
        this.context.fillStyle = "red";
        this.context.save();
        this.context.beginPath();
        for (let path of this.currPath) {
            this.context.moveTo(path[path.length - 1].X, path[path.length - 1].Y);
            for (let i = 0; i < path.length; i++) {
                this.context.lineTo(path[i].X, path[i].Y);
            }
        }
        this.context.closePath();
        this.context.fill();
        this.context.restore();
    }

    drawPaths(paths) {
        this.context.beginPath();
        for (let path of paths) {
            this.context.moveTo(path[path.length - 1].X, path[path.length - 1].Y);
            for (let i = 0; i < path.length; i++) {
                this.context.lineTo(path[i].X, path[i].Y);
            }
        }
        this.context.closePath();
        this.context.fill();
        this.context.beginPath();
        for (let path of paths) {
            this.context.moveTo(-path[path.length - 1].X, path[path.length - 1].Y);
            for (let i = 0; i < path.length; i++) {
                this.context.lineTo(-path[i].X, path[i].Y);
            }
        }
        this.context.closePath();
        this.context.fill();
    }

    drawPaint() {
        let solution = new ClipperLib.Paths();
        let co = new ClipperLib.ClipperOffset(2, 0.01);
        // let boneLine = ClipperLib.JS.Lighten(this.paintPath, 1);
        // console.log(boneLine, this.paintPath);
        // co.AddPath(boneLine, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etOpenRound);
        co.AddPath(this.paintLine, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etOpenRound);
        co.Execute(solution, this.lineWidth);
        // this.paintPath = solution;
        this.paintPath = ClipperLib.JS.Lighten(solution, 1);
        this.context.fillStyle = this.penType === "add" ? "blue" : "green";
        this.drawPaths(solution);
    }
}


// WEBPACK FOOTER //
// ./src/views/jewelryPendant/scripts/PaintDrawerMobile.js